import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isBetween from "dayjs/plugin/isBetween";
import weekOfYear from "dayjs/plugin/weekOfYear";

// Import only the locales we need
import "dayjs/locale/pl";
import "dayjs/locale/en";

import languageService from "../services/LanguageService";

// Initialize plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);

export class DateLocalizationHelper {
  private static instance: DateLocalizationHelper;
  private currentLocale: string;

  private constructor() {
    this.currentLocale = this.detectLocale();
    this.setDayjsLocale();
  }

  public static getInstance(): DateLocalizationHelper {
    if (!DateLocalizationHelper.instance) {
      DateLocalizationHelper.instance = new DateLocalizationHelper();
    }
    return DateLocalizationHelper.instance;
  }

  // Remove CR/LF and cap length before logging
  private sanitizeForLog(value: unknown, maxLen: number = 200): string {
    try {
      const s = String(value ?? "");
      const noCrLf = s.replace(/[\r\n]+/g, " ");
      return noCrLf.length > maxLen
        ? noCrLf.substring(0, maxLen) + "..."
        : noCrLf;
    } catch {
      return "[unprintable]";
    }
  }

  private detectLocale(): string {
    // Try to get language from service (cookie)
    const cookieLanguage = languageService.getCurrent();
    if (cookieLanguage) {
      return this.normalizeLocale(cookieLanguage);
    }

    // Fallback to browser language
    const browserLanguage =
      navigator.language || (navigator as any).userLanguage;
    if (browserLanguage) {
      return this.normalizeLocale(browserLanguage);
    }

    // Default fallback
    return "en";
  }

  private normalizeLocale(locale: string): string {
    // Map to known supported locales; anything else falls back to 'en'
    const localeMap: { [key: string]: string } = {
      pl: "pl",
      "pl-PL": "pl",
      en: "en",
      "en-US": "en",
      "en-GB": "en-gb",
    };

    const normalized = localeMap[locale] || localeMap[locale.split("-")[0]];
    return normalized || "en";
  }

  private setDayjsLocale(): void {
    try {
      dayjs.locale(this.currentLocale);
    } catch (error) {
      // Log with sanitized value, avoid embedding user input directly
      console.warn(
        "Failed to set dayjs locale, falling back to en. Requested locale: %s",
        this.sanitizeForLog(this.currentLocale)
      );
      dayjs.locale("en");
      this.currentLocale = "en";
    }
  }

  public getCurrentLocale(): string {
    return this.currentLocale;
  }

  public setLocale(locale: string): void {
    this.currentLocale = this.normalizeLocale(locale);
    this.setDayjsLocale();
  }

  public getMonthNames(): string[] {
    return dayjs.months();
  }

  public getShortMonthNames(): string[] {
    return dayjs.monthsShort();
  }

  public getWeekdayNames(): string[] {
    return dayjs.weekdays();
  }

  public getShortWeekdayNames(): string[] {
    return dayjs.weekdaysShort();
  }

  public getMinimalWeekdayNames(): string[] {
    return dayjs.weekdaysMin();
  }

  public formatDate(date: Date, format?: string): string {
    if (!date || isNaN(date.getTime())) {
      // Avoid logging raw object details
      console.error("Invalid date passed to formatDate.");
      return "Invalid Date";
    }
    return dayjs(date).format(format || "L");
  }

  public formatDateRange(
    startDate: Date,
    endDate: Date,
    format?: string
  ): string {
    if (!startDate || isNaN(startDate.getTime())) {
      console.error("Invalid start date passed to formatDateRange.");
      return "Invalid Date Range";
    }
    if (!endDate || isNaN(endDate.getTime())) {
      console.error("Invalid end date passed to formatDateRange.");
      return "Invalid Date Range";
    }

    const fmt = format || "L";
    return `${dayjs(startDate).format(fmt)} - ${dayjs(endDate).format(fmt)}`;
  }

  public getFirstDayOfWeek(): number {
    // Get the first day of week for current locale (0 = Sunday, 1 = Monday, etc.)
    return dayjs.localeData().firstDayOfWeek();
  }

  public isRTL(): boolean {
    // Check if current locale uses right-to-left text direction
    const rtlLocales = ["ar", "he", "fa", "ur"];
    return rtlLocales.includes(this.currentLocale);
  }

  public getDatePickerDirection(): "ltr" | "rtl" {
    return this.isRTL() ? "rtl" : "ltr";
  }

  public getToday(): Date {
    return new Date();
  }

  public isSameDate(date1: Date, date2: Date): boolean {
    return dayjs(date1).isSame(dayjs(date2), "day");
  }

  public isDateInRange(date: Date, startDate: Date, endDate: Date): boolean {
    const dayjsDate = dayjs(date);
    return dayjsDate.isBetween(dayjs(startDate), dayjs(endDate), "day", "()");
  }

  public addDays(date: Date, days: number): Date {
    return dayjs(date).add(days, "days").toDate();
  }

  public addMonths(date: Date, months: number): Date {
    return dayjs(date).add(months, "months").toDate();
  }

  public startOfMonth(date: Date): Date {
    return dayjs(date).startOf("month").toDate();
  }

  public endOfMonth(date: Date): Date {
    return dayjs(date).endOf("month").toDate();
  }

  public getDaysInMonth(date: Date): number {
    return dayjs(date).daysInMonth();
  }

  public getWeekOfYear(date: Date): number {
    return dayjs(date).week();
  }

  public parseDate(dateString: string, format?: string): Date | null {
    if (!dateString) return null;

    const formats = format
      ? [format]
      : ["YYYY-MM-DD", "L", "MM/DD/YYYY", "DD/MM/YYYY", "DD.MM.YYYY"];

    for (const fmt of formats) {
      const parsed = dayjs(dateString, fmt, true);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }

    // Log sanitized value to prevent CRLF issues in logs
    console.warn(
      "Could not parse date string: %s",
      this.sanitizeForLog(dateString)
    );
    return null;
  }

  public isValidDate(date: any): date is Date {
    return date instanceof Date && !isNaN(date.getTime());
  }

  public formatPublishedDate(
    input?: string | Date | null,
    outputFormat: string = "DD.MM.YYYY",
    options?: {
      prefix?: string | null; // e.g. 'Published on' or null for no prefix
      todayText?: string | null; // e.g. 'Published today' or null to format normally
      parsingFormats?: string | string[]; // additional strict formats to try if plain parse fails
      strictParsing?: boolean; // when trying parsingFormats, use strict parsing
      useUtcForComparison?: boolean; // compare "today" in UTC instead of local time
    }
  ): string {
    if (!input) return "";

    const {
      prefix = "Published on",
      parsingFormats,
      strictParsing = false,
      useUtcForComparison = false,
    } = options || {};

    const createDayjs = (val: string | Date) => {
      if (val instanceof Date) {
        return dayjs(val);
      }

      // First try loose ISO/native parse
      let parsed = dayjs(val);
      if (parsed.isValid()) return parsed;

      // If not valid, try provided strict parsing formats
      if (parsingFormats) {
        const formats = Array.isArray(parsingFormats)
          ? parsingFormats
          : [parsingFormats];
        for (const fmt of formats) {
          parsed = dayjs(val, fmt, strictParsing);
          if (parsed.isValid()) return parsed;
        }
      }

      // Last resort: return invalid dayjs
      return parsed;
    };

    const parsed = createDayjs(input as any);
    if (!parsed.isValid()) {
      console.warn(
        "formatPublishedDate: could not parse date: %s",
        this.sanitizeForLog(input)
      );
      return "";
    }

    // Note: useUtcForComparison is reserved for future behavior; formatting remains by outputFormat
    const formatted = parsed.format(outputFormat);
    return prefix ? `${prefix} ${formatted}` : formatted;
  }
}
